import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useRegister } from '../hooks/useAuth'
import SocialLoginButtons from '../components/SocialLoginButtons'
import SEOHead from '../components/SEOHead'

export default function RegisterPage() {
  const navigate = useNavigate()
  const { mutate: register, isPending, error } = useRegister()
  const [params] = useSearchParams()

  // Prefill from query string so the BookingModal's "Create with password"
  // option round-trips the data the visitor already entered. The visitor
  // only has to add a password (and confirm).
  const [form, setForm] = useState({
    first_name: params.get('first_name') ?? '',
    last_name:  params.get('last_name')  ?? '',
    email:      params.get('email')      ?? '',
    phone:      params.get('phone')      ?? '',
    password: '',
    password_confirmation: '',
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Visitors arriving from the post-booking flow have already booked,
    // so send them to the portal dashboard rather than the legacy /book
    // funnel. Anyone who registered without a pending booking still sees
    // /portal/dashboard as the right home.
    register({ ...form }, { onSuccess: () => navigate('/portal/dashboard') })
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-16 bg-gray-50">
      <SEOHead
        title="Create Your Account"
        description="Sign up for Nala to book in-home veterinary visits for your pets."
        noIndex
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create your account</h1>
          <p className="text-gray-500 mt-2">Start booking home vet visits for your pets.</p>
        </div>

        <div className="card space-y-6">
          {/* ── Social sign-up ───────────────────────────────────────────── */}
          <SocialLoginButtons action="sign_up" />

          {/* ── Divider ──────────────────────────────────────────────────── */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400 uppercase tracking-wide">
                or sign up with email
              </span>
            </div>
          </div>

          {/* ── Email / password form ─────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="first_name">First name</label>
                <input
                  id="first_name" name="first_name" type="text" required autoComplete="given-name"
                  className="input" value={form.first_name} onChange={handleChange}
                />
              </div>
              <div>
                <label className="label" htmlFor="last_name">Last name</label>
                <input
                  id="last_name" name="last_name" type="text" required autoComplete="family-name"
                  className="input" value={form.last_name} onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="email">Email address</label>
              <input
                id="email" name="email" type="email" required autoComplete="email"
                className="input" value={form.email} onChange={handleChange}
              />
            </div>

            <div>
              <label className="label" htmlFor="phone">Phone number</label>
              <input
                id="phone" name="phone" type="tel" required autoComplete="tel"
                className="input" placeholder="+1 (555) 000-0000"
                value={form.phone} onChange={handleChange}
              />
            </div>

            <div>
              <label className="label" htmlFor="password">Password</label>
              <input
                id="password" name="password" type="password" required autoComplete="new-password"
                className="input" value={form.password} onChange={handleChange}
              />
            </div>

            <div>
              <label className="label" htmlFor="password_confirmation">Confirm password</label>
              <input
                id="password_confirmation" name="password_confirmation" type="password" required
                autoComplete="new-password" className="input"
                value={form.password_confirmation} onChange={handleChange}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
                {(error as Error).message || 'Something went wrong. Please try again.'}
              </p>
            )}

            <button type="submit" disabled={isPending} className="btn-primary w-full py-3">
              {isPending ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-700 font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
